import React from 'react';

import PropTypes from 'prop-types';

import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import { MenuList, MenuCategory } from '@adsk/alloy-react-menu';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import { entityShape, pluginShape } from './helpers/propTypes';
import InlineLoader from './InlineLoader';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';
import { useRelationshipsContext } from './providers/RelationshipsProvider';

const getPluginsEntityMap = ({ plugins, relationshipsData, entitiesCache }) => {
  return plugins
    .map((plugin) => {
      const relationships = relationshipsData.filter(
        ({ domain, type }) =>
          domain === plugin.metadata.domain && type === plugin.metadata.type,
      );
      const allEntities = relationships.map(
        ({ domain, type, entityId }) =>
          entitiesCache?.[domain]?.[type]?.[entityId],
      );

      const loading = allEntities.some((e) => e?.loading);
      const entities = allEntities.filter((e) => e?.entity);
      return { plugin, entities, loading };
    })
    .filter(({ entities, loading }) => loading || entities.length);
};

const EntityGroupInTooltip = ({ entities, plugin, hasDivider, T }) => {
  const { EntityMenuItem } = plugin;
  return entities.length ? (
    <MenuCategory title={<plugin.PluralText />}>
      <MenuList style={{ paddingBottom: 12 }}>
        {EntityMenuItem ? (
          entities.map(({ entity }) => (
            <EntityMenuItem
              entity={entity}
              renderIcon={plugin.renderIcon}
              key={entity.id}
            />
          ))
        ) : (
          <div style={{ padding: '0 15px' }}>
            {T.translate('REFERENCES', { context: entities.length })}
          </div>
        )}
      </MenuList>
      {hasDivider && <Divider />}
    </MenuCategory>
  ) : null;
};
EntityGroupInTooltip.propTypes = {
  plugin: pluginShape.isRequired,
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      entity: entityShape,
    }),
  ),
  hasDivider: PropTypes.bool,
  T: PropTypes.object,
};

const LinkedReferencesCell = ({
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
  plugins,
  style,
  className,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { relationshipDataBySource } = useRelationshipsContext();
  const { relationshipsData = [], loading: relationshipLoading } =
    relationshipDataBySource?.[sourceEntityDomain]?.[sourceEntityType]?.[
      sourceEntityId
    ] || {};

  const { entitiesCache } = useEntitiesCacheContext();

  const pluginsEntityMap = getPluginsEntityMap({
    plugins,
    relationshipsData,
    entitiesCache,
  });
  const size = Object.values(pluginsEntityMap).reduce(
    (acc, data) => acc + data.entities.length,
    0,
  );
  const entitiesLoading = pluginsEntityMap.some(({ loading }) => loading);
  if (relationshipLoading || entitiesLoading) {
    return <InlineLoader />;
  }
  const firstItem = pluginsEntityMap.length
    ? pluginsEntityMap[0].entities[0]
    : null;
  if (!firstItem) {
    return '-';
  }

  const tooltipContent = () => {
    const mappedEntityTypes = Object.values(pluginsEntityMap).filter(
      ({ entities }) => entities.length,
    );
    return (
      <>
        {mappedEntityTypes.map(({ entities, plugin }, index) => (
          <EntityGroupInTooltip
            entities={entities}
            plugin={plugin}
            key={plugin.metadata.type}
            hasDivider={index < mappedEntityTypes.length - 1}
            T={T}
          />
        ))}
      </>
    );
  };

  const tooltip = tooltipContent();
  const cellValue = (
    <span
      css={[{ display: 'flex', maxWidth: '100%', whiteSpace: 'nowrap' }, style]}
      className={className}
      data-testid="linkedReferenceCellValue"
    >
      <span
        css={{
          color: theme.colors.adskBlue700,
          cursor: 'pointer',
          '&:hover': {
            color: theme.colors.adskBlue500,
          },
        }}
      >
        {T.translate('REFERENCES', { context: size })}
      </span>
    </span>
  );

  return (
    <Flyout
      content={tooltip}
      trigger={Flyout.TRIGGER_EVENTS.HOVER}
      triggerTarget={Flyout.TRIGGER_TARGETS.TOOLTIP}
      style={{
        padding: '1px 0',
        maxHeight: 272,
        overflowY: 'auto',
      }}
    >
      {cellValue}
    </Flyout>
  );
};

LinkedReferencesCell.propTypes = {
  /**
   * The entity ID we want to get linked entities for.
   */
  sourceEntityId: PropTypes.string.isRequired,
  /**
   * The source entity domain, as used by relationship service.
   */
  sourceEntityDomain: PropTypes.string.isRequired,
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string.isRequired,
  /**
   * The plugins we want to get linked entities for.
   */
  plugins: PropTypes.arrayOf(pluginShape).isRequired,
  /**
   * Styles applied to the cell value
   * */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * Class applied to the cell value
   * */
  className: PropTypes.string,
};

export default LinkedReferencesCell;
